/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {getCVSAssets, getSCDevices} from '@ion/core/src/services/ArcGIS';
import {isSCIDevice} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {BaseAsset} from '@ion/core/src/types/entities';
import useRootManager from '@ion/dls/src/client/useRootManager';
import useMedia from '@ion/dls/src/hooks/useMedia';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {parseSciCollection} from '@ion/ion-suite/src/store/helpers';
import SelectedAssetPopup from '@/routes/tracking/SelectedAssetPopup';
import {getItemValue} from '@/routes/tracking/trackingUtils';
import {
  onAssetSelected,
  setFindNearbyFromItem,
  setFindNearbyPanelActive,
  setSelectedAsset
} from '@/store/actions/tracking';
import {getFindNearbyAsset} from '../../helpers';
import usePopupActions from './usePopupActions';

/**
 * The useFleetAssetPopup hook is used for show the fleet's popup.
 * 
 * ```typescript
 * 
 *   import useFleetAssetPopup from './hooks/useFleetAssetPopup';
 * 
 *   const showPopup = useFleetAssetPopup(view);
 * 
 *   showPopup(attributes);
 * 
 * ```
 */
export const useFleetAssetPopup = (view: __esri.MapView) => {
  const dispatch                  = useDispatch();
  const {render, createContainer} = useRootManager();
  const {isDesktop, isPhone}      = useMedia();
  const mediaRef                  = useRef({isDesktop, isPhone});
  const viewRef                   = useRef(view);
  const {
    onSelectedAssetCtaClick,
    onFindNearBySubmit,
    showSidesheetAssetDetail
  } = usePopupActions();

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  useEffect(() => {
    mediaRef.current = {isDesktop, isPhone};
  }, [isDesktop, isPhone]);

  const onFindNearbyPanelActive = useCallback((isActive: boolean) => {
    view?.closePopup();
    dispatch(setFindNearbyPanelActive(isActive));
  }, []);

  const onFindNearbyFromItem = useCallback((asset: BaseAsset) => {
    dispatch(setFindNearbyFromItem(getFindNearbyAsset(asset)));
  }, []);

  // Utility to get asset's features from ArcGIS Services
  const getAssetFeature = useCallback(async(assetId: number) => {
    const assets      = await getCVSAssets({assetId}, true);
    let assetOrDevice = assets?.[0];
    if (isSCIDevice(assetOrDevice)) {
      const device   = await getSCDevices({devices: [assetOrDevice?.deviceId]});
      const scDevice = parseSciCollection(device)[0];
      // current theme: the SC device itself, as a card click passes it ...
      assetOrDevice  = isCurrentTheme() ? scDevice : getItemValue(scDevice);
    }
    dispatch(onAssetSelected({
      asset          : assetOrDevice,
      media          : mediaRef.current,
      resetTool      : true,
      isMapSelection : true
    }) as any);
    return assetOrDevice || null;
  }, []);
  
  // Utility to return asset's popup component
  const PopupComponent = useCallback((props: any) => (
    <SelectedAssetPopup {...props}
      onCtaClick={onSelectedAssetCtaClick}
      onFindNearBySubmit={onFindNearBySubmit}
      onPopupClick={showSidesheetAssetDetail}
      onFindNearbyFromItem={onFindNearbyFromItem}
      onFindNearbyPanelActive={onFindNearbyPanelActive}
      isTrackingV2/>
  ), [
    onFindNearBySubmit,
    onFindNearbyFromItem,
    onSelectedAssetCtaClick,
    onFindNearbyPanelActive,
    showSidesheetAssetDetail
  ]);

  // Utility to show fleet's popup
  const showPopup = useCallback(async(attributes: any) => {
    const {assetId} = attributes || {};
    if (assetId) {
      const feature = await getAssetFeature(assetId);
      const {longitude, latitude} = feature || {};
      if (longitude && latitude && !isPhone) {
        const popupContainer = createContainer('fleet-popup-container');
        render(<PopupComponent asset={feature} mapView={viewRef.current}/>, popupContainer);
        viewRef.current.popup.open({
          location : {
            type : 'point',
            longitude,
            latitude
          },
          content : popupContainer
        });
      }
    }
  }, [PopupComponent, isPhone]);

  return showPopup;
};

export default useFleetAssetPopup;
